// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UserService } from 'src/services/user-service';
import { NotificationService } from 'src/services/notification-service';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { IPartner } from '../../../types/partners';
import { D3eService } from '../../../services/d3e-service';
import { IUser, SubscriptionPaymentSource } from '../../../types/user';
import { StripeProductTier } from '../../../types/subscription';

@Component({
    selector: 'app-edit-org',
    templateUrl: './edit-org.component.html',
    styleUrls: ['./edit-org.component.scss'],
    standalone: false
})
export class EditOrgComponent {
  form: UntypedFormGroup;
  checkingEmailExists = false;
  partners: IPartner[] = [];
  user: IUser = null;

  fHeader: string;
  fCompany: string;
  fEmail: string;
  fEExists: string;
  fEInvalid: string;
  fFName: string;
  fLName: string;
  fCancel: string;
  fSave: string;
  canSetEdition = false;
  currentEdition = '';

  constructor(
    public dialogRef: MatDialogRef<EditOrgComponent>,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private d3eService: D3eService,
    private notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService
  ) {
    this.translate.get(['FIRST', 'LAST', 'CANCEL_CAP', 'SAVE_CAP', 'EO_HEADER', 'EO_COMPANY', 'EO_EMAIL', 'EO_EMAIL_EXISTS',
      'EO_EMAIL_INVALID']).subscribe(vals => {
      this.fHeader = vals['EO_HEADER'];
      this.fCompany = vals['EO_COMPANY'];
      this.fEmail = vals['EO_EMAIL'];
      this.fEExists = vals['EO_EMAIL_EXISTS'];
      this.fEInvalid = vals['EO_EMAIL_INVALID'];
      this.fFName = vals['FIRST'];
      this.fLName = vals['LAST'];
      this.fCancel = vals['CANCEL_CAP'];
      this.fSave = vals['SAVE_CAP'];
    });
    this.d3eService.getPartners().subscribe(result => {
      this.partners = result.partners;
    });
    this.userService.getUser().subscribe(user => {
      this.user = user;
    });
    this.form = this.formBuilder.group({
      company: ['', Validators.required],
      contact_email: ['', [Validators.required, Validators.pattern(CustomValidators.emailPattern),
        CustomValidators.maxByteLength(255)]],
      reserve_domain: [false, Validators.nullValidator],
      enable_endpoint_map: [false],
      partnerId: ['-1'],
      edition: ['']
    });
    this.form.controls['company'].setValue(this.data.user.company);
    this.form.controls['contact_email'].setValue(this.data.user.contact_email);
    this.form.controls['reserve_domain'].setValue(this.data.user?.reserve_domain ?? false);
    this.form.controls['enable_endpoint_map'].setValue(this.data.group?.enable_endpoint_map ?? false);

    if (this.data.user.partnerId == null) {
      this.form.controls['partnerId'].setValue('-1');
    } else {
      this.form.controls['partnerId'].setValue(this.data.user.partnerId.toString());
    }
    if ( this.data.choosePlan &&
         this.data.user.subscriptions !== undefined &&
         this.data.user.subscriptions != null &&
         this.data.user.subscriptions.length > 0) {
      // We want to disable this if there is ANY stripe sub in this customer
      this.canSetEdition = !this.data.user.subscriptions.some(sub =>
          (sub.payment_source === SubscriptionPaymentSource.STRIPE ||
            sub.tier === StripeProductTier.HARDWARE ||
            sub.tier === StripeProductTier.HARDWARE2 ||
            sub.tier === StripeProductTier.BITLOCKER ||
            sub.tier === StripeProductTier.FDE ||
            sub.tier === StripeProductTier.AGENT));
    }
    if ( this.canSetEdition ) {
      let currentSelected = this.translate.instant('ADS_PERSONAL');
      if (this.data.user.business_edition) {
        currentSelected = this.translate.instant('ADS_PREMIUM');
      }
      this.currentEdition = ' ( ' + this.translate.instant('CURRENTLY') + ' ' + currentSelected + ' )';
    }
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onSubmit() {
    const newUser = this.form.value;
    newUser.id = this.data.user.id;
    newUser.email = this.data.user.email;
    newUser.fname = this.data.user.fname;
    newUser.lname = this.data.user.lname;
    if (!this.user.org_type.create_child) {
      newUser.partnerId = null;
    }
    this.dialogRef.close(newUser);
  }

  get contact_email() {
    return this.form.controls['contact_email'];
  }

}
